import React from 'react';
import { motion } from 'framer-motion';
import { Award, Wrench, Users, Rocket, X, CheckCircle, CreditCard, Star, Clock, Shield } from 'lucide-react';

// Window.PaystackPop is typed globally in src/types/paystack.d.ts.

const upgrades = [
  {
    title: 'Premium Certificate',
    price: '₦3,000',
    icon: Award,
    description: 'Official LSEM certification with enhanced credibility',
    features: ['Verified digital certificate', 'LinkedIn integration', 'Employer recognition'],
    detailedDescription: 'Upgrade your free Certificate of Participation to a premium, professionally designed certificate that carries more weight with employers and industry professionals. This certificate includes verification features and can be easily shared on professional networks.',
    benefits: [
      'Professional certificate design with LSEM branding',
      'Digital verification system for authenticity',
      'LinkedIn certificate integration',
      'Enhanced credibility with employers',
      'Lifetime access to certificate portal',
      'Replacement certificate service'
    ],
    deliveryTime: '24-48 hours after payment',
    paymentCode: 'c2c-premium-cert'
  },
  {
    title: 'Digital Toolkit',
    price: '₦10,000',
    icon: Wrench,
    description: 'Essential digital tools and templates for export',
    features: ['Export templates', 'Calculation tools', 'Market research guides'],
    detailedDescription: 'Get access to a comprehensive collection of digital tools, templates, and resources that will streamline your export operations. These professionally designed materials will save you time and ensure you follow best practices.',
    benefits: [
      'Export documentation templates (Invoice, Packing List, etc.)',
      'Pricing calculation spreadsheets',
      'Market research templates',
      'Buyer communication templates',
      'Export checklist and workflows',
      'Digital resource library access',
      'Regular updates and new tools'
    ],
    deliveryTime: 'Instant access after payment',
    paymentCode: 'c2c-digital-toolkit'
  },
  {
    title: 'Mentorship',
    price: 'From ₦10,000',
    icon: Users,
    description: 'One-on-one guidance from export professionals',
    features: ['Personal mentor', 'Monthly sessions', 'Career guidance'],
    detailedDescription: 'Connect with experienced export professionals who will provide personalized guidance for your export journey. Choose from different mentorship packages based on your needs and budget.',
    benefits: [
      'One-on-one mentorship sessions',
      'Personalized export business plan development',
      'Industry insights and market intelligence',
      'Network introductions and connections',
      'Career guidance and professional development',
      'Email and WhatsApp support between sessions',
      'Access to mentor\'s professional network'
    ],
    deliveryTime: 'Mentor assignment within 3-5 business days',
    paymentCode: 'c2c-mentorship',
    packages: [
      { name: 'Basic Mentorship', price: '₦10,000', sessions: '2 sessions (1 hour each)' },
      { name: 'Standard Mentorship', price: '₦18,000', sessions: '4 sessions (1 hour each)' },
      { name: 'Premium Mentorship', price: '₦30,000', sessions: '6 sessions (1.5 hours each)' }
    ]
  },
  {
    title: 'Export Setup Support',
    price: '₦120,000',
    icon: Rocket,
    description: 'Complete support to launch your export business',
    features: ['Business registration', 'Documentation setup', 'First shipment support'],
    detailedDescription: 'Get comprehensive support to set up your export business from scratch. Our experts will guide you through every step of the process, from business registration to your first successful shipment.',
    benefits: [
      'Business registration guidance and support',
      'Export license and permit assistance',
      'Bank account setup for export transactions',
      'Product sourcing and supplier connections',
      'First buyer identification and connection',
      'Documentation preparation for first shipment',
      'Logistics and shipping arrangement support',
      '3 months of post-setup support'
    ],
    deliveryTime: '2-4 weeks for complete setup',
    paymentCode: 'c2c-export-setup'
  }
];

const C2CUpgrade = () => {
  const [selectedUpgrade, setSelectedUpgrade] = React.useState<typeof upgrades[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedMentorshipPackage, setSelectedMentorshipPackage] = React.useState<string>('');
  const [customerEmail, setCustomerEmail] = React.useState('');
  const [paystackReady, setPaystackReady] = React.useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = React.useState(false);
  const [paymentError, setPaymentError] = React.useState('');

  // Load Paystack script
  React.useEffect(() => {
    const checkPaystack = () => {
      if (window.PaystackPop) {
        setPaystackReady(true);
        return true;
      }
      return false;
    };

    // First check if already loaded
    if (checkPaystack()) return;

    // If not loaded, create script tag
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    
    script.onload = () => {
      if (checkPaystack()) {
        console.log('Paystack loaded successfully');
      } else {
        console.error('Paystack script loaded but PaystackPop not available');
        setPaymentError('Payment processor failed to load. Please refresh the page.');
      }
    };

    script.onerror = () => {
      console.error('Failed to load Paystack script');
      setPaymentError('Failed to load payment processor. Please refresh the page.');
    };

    document.body.appendChild(script);

    return () => {
      // Only remove if script exists
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleLearnMore = (upgrade: typeof upgrades[0]) => {
    setSelectedUpgrade(upgrade);
    setIsModalOpen(true);
    setPaymentError(''); // Clear any previous errors
  };

  const handlePayment = () => {
    if (!selectedUpgrade || !customerEmail) {
      setPaymentError('Please provide your email address to proceed with payment.');
      return;
    }

    // Verify Paystack is ready
    if (!window.PaystackPop) {
      setPaymentError('Payment processor not ready. Please wait a moment and try again.');
      return;
    }

    if (selectedUpgrade.title === 'Mentorship' && !selectedMentorshipPackage) {
      setPaymentError('Please select a mentorship package to proceed.');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError('');
    
    let finalPrice = selectedUpgrade.price.replace(/[^0-9]/g, '');
    let packageName = selectedUpgrade.title;
    
    // Handle mentorship package selection
    if (selectedUpgrade.title === 'Mentorship' && selectedMentorshipPackage) {
      const selectedPackage = selectedUpgrade.packages?.find(pkg => pkg.name === selectedMentorshipPackage);
      if (selectedPackage) {
        finalPrice = selectedPackage.price.replace(/[^0-9]/g, '');
        packageName = selectedPackage.name;
      }
    }
    
    const amount = parseInt(finalPrice) * 100; // Convert to kobo
    const reference = `C2C_${selectedUpgrade.paymentCode}_${Date.now()}`;
    
    try {
      // Initialize Paystack payment
      const handler = window.PaystackPop.setup({
        key: 'pk_live_8a4c486aa818a4bb95c24504316341f54e6c4e91', // Replace with your actual Paystack public key
        email: customerEmail,
        amount: amount,
        currency: 'NGN',
        ref: reference,
        metadata: {
          custom_fields: [
            {
              display_name: "Service",
              variable_name: "service",
              value: packageName
            },
            {
              display_name: "Program",
              variable_name: "program", 
              value: "Campus2Container"
            },
            {
              display_name: "Customer Email",
              variable_name: "customer_email",
              value: customerEmail
            }
          ]
        },
        callback: function(response) {
          console.log('Payment successful:', response);
          setIsProcessingPayment(false);
          alert(`Payment successful! Reference: ${response.reference}\n\nYou will receive your ${packageName} within ${selectedUpgrade.deliveryTime}.\n\nA confirmation email will be sent to ${customerEmail}.`);
          setIsModalOpen(false);
          setSelectedUpgrade(null);
          setCustomerEmail('');
          setSelectedMentorshipPackage('');
          setPaymentError('');
        },
        onClose: function() {
          console.log('Payment window closed');
          setIsProcessingPayment(false);
        }
      });

      handler.openIframe();
    } catch (error) {
      console.error('Payment initialization error:', error);
      setPaymentError('Failed to initialize payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  return (
    <>
      <section id="upgrade" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-gray-900 sm:text-4xl"
            >
              Want More Than Just Training?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mt-4 text-xl text-gray-600"
            >
              Optional premium services to accelerate your export journey
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {upgrades.map((upgrade, index) => (
              <motion.div
                key={upgrade.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                    <upgrade.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {upgrade.title}
                  </h3>
                  
                  <div className="text-2xl font-bold text-blue-600 mb-4">
                    {upgrade.price}
                  </div>
                  
                  <p className="text-gray-600 mb-6">
                    {upgrade.description}
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    {upgrade.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => handleLearnMore(upgrade)}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300"
                  >
                    Learn More
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Modal with Comprehensive Details */}
      {isModalOpen && selectedUpgrade && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedUpgrade(null);
                    setCustomerEmail('');
                    setSelectedMentorshipPackage('');
                    setPaymentError('');
                  }}
                  title="Close modal"
                  aria-label="Close modal"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none shadow-lg p-2"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  {/* Header */}
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mr-6">
                      <selectedUpgrade.icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900">
                        {selectedUpgrade.title}
                      </h3>
                      <p className="text-2xl font-semibold text-blue-600">
                        {selectedUpgrade.price}
                      </p>
                    </div>
                  </div>

                  {/* Detailed Description */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">About This Service</h4>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedUpgrade.detailedDescription}
                    </p>
                  </div>

                  {/* Benefits Grid */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-gray-900 mb-6">What You'll Get:</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedUpgrade.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-start bg-gray-50 rounded-lg p-4">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mentorship Package Selection */}
                  {selectedUpgrade.title === 'Mentorship' && selectedUpgrade.packages && (
                    <div className="mb-8">
                      <h4 className="text-xl font-semibold text-gray-900 mb-4">Choose Your Mentorship Package:</h4>
                      <div className="grid gap-4">
                        {selectedUpgrade.packages.map((pkg, index) => (
                          <div
                            key={pkg.name}
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-300 ${
                              selectedMentorshipPackage === pkg.name
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setSelectedMentorshipPackage(pkg.name)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className="font-semibold text-gray-900">{pkg.name}</h5>
                                <p className="text-sm text-gray-600">{pkg.sessions}</p>
                              </div>
                              <div className="text-lg font-bold text-blue-600">{pkg.price}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Service Details */}
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">Delivery Time</div>
                      <div className="font-semibold text-gray-900">{selectedUpgrade.deliveryTime}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <Shield className="h-6 w-6 text-green-600 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">Guarantee</div>
                      <div className="font-semibold text-gray-900">100% Satisfaction</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <Star className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">Support</div>
                      <div className="font-semibold text-gray-900">Lifetime Access</div>
                    </div>
                  </div>

                  {/* Payment Section */}
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Complete Your Purchase</h4>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address (Required for payment)
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="Enter your email address"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                      />
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200 mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">Service:</span>
                        <span className="text-gray-700">
                          {selectedUpgrade.title === 'Mentorship' && selectedMentorshipPackage
                            ? selectedMentorshipPackage
                            : selectedUpgrade.title}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">Total Amount:</span>
                        <span className="text-xl font-bold text-blue-600">
                          {selectedUpgrade.title === 'Mentorship' && selectedMentorshipPackage
                            ? selectedUpgrade.packages?.find(pkg => pkg.name === selectedMentorshipPackage)?.price
                            : selectedUpgrade.price}
                        </span>
                      </div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4 mb-6">
                      <div className="flex items-start">
                        <Shield className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-blue-900 mb-1">Secure Payment</h5>
                          <p className="text-sm text-blue-700">
                            Your payment is processed securely through Paystack. We never store your payment information.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Error Display */}
                    {paymentError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start">
                          <X className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                          <div>
                            <h5 className="font-semibold text-red-900 mb-1">Payment Error</h5>
                            <p className="text-sm text-red-700">{paymentError}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Paystack Loading Status */}
                    {!paystackReady && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start">
                          <Clock className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                          <div>
                            <h5 className="font-semibold text-yellow-900 mb-1">Loading Payment System</h5>
                            <p className="text-sm text-yellow-700">Please wait while we prepare the payment system...</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    >
                      Maybe Later
                    </button>
                    <button
                      onClick={handlePayment}
                      disabled={!customerEmail || (selectedUpgrade.title === 'Mentorship' && !selectedMentorshipPackage) || !paystackReady || isProcessingPayment}
                      className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessingPayment ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-5 w-5 mr-2" />
                          Pay Now
                        </>
                      )}
                    </button>
                  </div>

                  {/* Contact Support */}
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      Need help? Contact us at{' '}
                      <a href="mailto:info@lsem.org.ng" className="text-blue-600 hover:text-blue-700">
                        info@lsem.org.ng
                      </a>
                      {' '}or{' '}
                      <a href="https://wa.me/2349058681595" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700">
                        WhatsApp us
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default C2CUpgrade;